import { QueryClient } from "@tanstack/react-query";
import type { ApiError } from "./apiErrors";

const MAX_RETRIES = 3;

/**
 * Decide whether TanStack Query should retry a failed query.
 *
 * Returns false for errors that will never succeed on retry:
 *  - 429 (rate-limited) / rateLimitHit flag
 *  - 401 / 403 (auth errors)
 *  - REAUTH_REQUIRED message
 *  - "Rate limit exceeded" in the message
 *
 * Transient errors (5xx, network failures) get up to 3 retries.
 */
export function shouldRetry(failureCount: number, error: Error): boolean {
  const apiError = error as ApiError;

  // Never retry rate-limit errors
  if (apiError.status === 429 || apiError.rateLimitHit) {
    return false;
  }

  // Never retry auth errors
  if (apiError.status === 401 || apiError.status === 403) {
    return false;
  }

  // Never retry explicit reauth signals
  if (apiError.message === "REAUTH_REQUIRED") {
    return false;
  }

  // Catch rate-limit messages from edge functions
  if (apiError.message?.includes("Rate limit exceeded")) {
    return false;
  }

  // Transient errors: retry up to MAX_RETRIES times
  return failureCount < MAX_RETRIES;
}

// Create a shared QueryClient instance that can be used across the app
// This allows components like AuthContext to cancel/clear queries on logout
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
    },
    mutations: {
      retry: false,
    },
  },
});
